/**
 * v4.37.0 — Global Broadcast surface mount.
 *
 * Mounted ONCE inside the authenticated Layout shell. Polls
 * /broadcasts/active for STICKY and MODAL surfaces (inline is
 * served by the Welcome route as an `announcement` event — no
 * polling needed there).
 *
 * Behavior:
 *   • One sticky banner rendered at most (highest priority wins).
 *   • One modal shown at most (highest priority not yet dismissed
 *     in this surface-poll cycle).
 *   • Dismissals call the backend AND optimistically remove the
 *     broadcast from local state so the user doesn't see it flash
 *     back on the next render.
 *
 * Polling cadence: on mount + every 5 minutes. Heavy enough to
 * pick up newly published broadcasts within a few minutes, light
 * enough to never feel chatty.
 */
import { useEffect, useState, useCallback } from 'react';
import { broadcastsAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { StickyBroadcastBar, BroadcastModal } from './BroadcastPreview';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const BroadcastSurfaces = () => {
    const { user } = useAuth();
    const [stickyList, setStickyList] = useState([]);
    const [modalList, setModalList] = useState([]);

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const [s, m] = await Promise.all([
                broadcastsAPI.listActive('sticky'),
                broadcastsAPI.listActive('modal'),
            ]);
            setStickyList(s.data || []);
            setModalList(m.data || []);
        } catch (_e) {
            // Silent — broadcasts are purely informational. A failure
            // here must never disrupt the rest of the app.
        }
    }, [user]);

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [refresh]);

    const handleDismiss = async (id, kind) => {
        // Optimistic update — the user expects instant feedback.
        if (kind === 'sticky') setStickyList((prev) => prev.filter((b) => b.id !== id));
        if (kind === 'modal')  setModalList((prev) => prev.filter((b) => b.id !== id));
        try {
            await broadcastsAPI.dismiss(id);
        } catch (_e) {
            // Re-fetch to reconcile on failure.
            refresh();
        }
    };

    if (!user) return null;

    const topSticky = stickyList[0] || null;
    const topModal = modalList[0] || null;

    return (
        <>
            {topSticky && (
                <StickyBroadcastBar
                    key={topSticky.id}  /* Force fresh mount when the top broadcast changes,
                                          * so any in-flight transitions/animations replay and
                                          * downstream DOM observers see a clean unmount/remount
                                          * cycle when one broadcast is dismissed and the next
                                          * priority takes its place. */
                    broadcast={topSticky}
                    onDismiss={(id) => handleDismiss(id, 'sticky')}
                />
            )}
            {topModal && (
                <BroadcastModal
                    key={topModal.id}
                    broadcast={topModal}
                    onDismiss={(id) => handleDismiss(id, 'modal')}
                />
            )}
        </>
    );
};

export default BroadcastSurfaces;
